import { client, handleRequestBody } from "./client.ts";
import type { Invite, InviteRole } from "./invites.ts";

export interface WorksiteWorker {
	id: string;
	name: string;
	job_title?: string;
	role?: string;
	user_id: string | null;
	user_email?: string;
	time_clock_enabled: boolean;
	deactivated_at: string | null;
	created_at: string;
	updated_at: string;
}

interface WorkerResponse {
	worker: WorksiteWorker;
}

export interface ListWorkersResponse {
	count: number;
	items: WorksiteWorker[];
}

export function listWorkers(worksiteId: string): Promise<ListWorkersResponse> {
	return handleRequestBody(() =>
		client.get<ListWorkersResponse>(`/worksites/${worksiteId}/workers`),
	);
}

export interface CreateWorkerInput {
	name: string;
	jobTitle?: string;
}

export async function createWorker(
	worksiteId: string,
	input: CreateWorkerInput,
): Promise<WorksiteWorker> {
	const { worker } = await handleRequestBody(() =>
		client.post<WorkerResponse>(`/worksites/${worksiteId}/workers`, {
			name: input.name,
			job_title: input.jobTitle ?? "",
		}),
	);
	return worker;
}

export interface UpdateWorkerInput {
	name?: string;
	jobTitle?: string;
}

export async function updateWorker(
	worksiteId: string,
	workerId: string,
	input: UpdateWorkerInput,
): Promise<WorksiteWorker> {
	const { worker } = await handleRequestBody(() =>
		client.patch<WorkerResponse>(
			`/worksites/${worksiteId}/workers/${workerId}`,
			{ name: input.name, job_title: input.jobTitle },
		),
	);
	return worker;
}

export async function setWorkerTimeClock(
	worksiteId: string,
	workerId: string,
	timeClockEnabled: boolean,
): Promise<WorksiteWorker> {
	const { worker } = await handleRequestBody(() =>
		client.patch<WorkerResponse>(
			`/worksites/${worksiteId}/workers/${workerId}`,
			{ time_clock_enabled: timeClockEnabled },
		),
	);
	return worker;
}

export async function deactivateWorker(
	worksiteId: string,
	workerId: string,
): Promise<WorksiteWorker> {
	const { worker } = await handleRequestBody(() =>
		client.post<WorkerResponse>(
			`/worksites/${worksiteId}/workers/${workerId}/deactivate`,
			{},
		),
	);
	return worker;
}

export async function reactivateWorker(
	worksiteId: string,
	workerId: string,
): Promise<WorksiteWorker> {
	const { worker } = await handleRequestBody(() =>
		client.post<WorkerResponse>(
			`/worksites/${worksiteId}/workers/${workerId}/reactivate`,
			{},
		),
	);
	return worker;
}

export interface InviteWorkerInput {
	email: string;
	role: InviteRole;
}

export async function inviteWorker(
	worksiteId: string,
	workerId: string,
	input: InviteWorkerInput,
): Promise<Invite> {
	const { invite } = await handleRequestBody(() =>
		client.post<{ invite: Invite }>(
			`/worksites/${worksiteId}/workers/${workerId}/invite`,
			{ email: input.email, role: input.role },
		),
	);
	return invite;
}
